import { useCallback, useEffect, useRef, useState } from 'react';

import { gql, useLazyQuery } from '@apollo/client';
import { AutoComplete, AutoCompleteProps } from 'primereact/autocomplete';

import { MIN_SEARCH_LENGTH, SELECTION_LIMIT } from '@/constants';
import { Recipient } from '@/types.gql';

const GET_RECIPIENTS = gql`
  query GET_RECIPIENTS($skip: Int!, $take: Int!, $where: RecipientFilterInput) {
    recipients(skip: $skip, take: $take, where: $where) {
      items {
        id
        tag
      }
    }
  }
`;

type RecipientsAutoCompleteProps = Omit<AutoCompleteProps, 'defaultValue'> & {
  className?: string;
  label?: string;
  defaultValue?: Recipient;
};

export const RecipientsAutoComplete = ({
  className,
  label,
  defaultValue,
  onSelect,
  onChange: externalOnChange,
  ...autoCompleteProps
}: RecipientsAutoCompleteProps) => {
  const [selected, setSelected] = useState<Recipient | undefined>(defaultValue);
  const [filtered, setFiltered] = useState<Recipient[]>([]);
  const [inputValue, setInputValue] = useState(defaultValue?.tag);
  const userChangedRef = useRef(false);

  const [query] = useLazyQuery(GET_RECIPIENTS);

  useEffect(() => {
    if (userChangedRef.current) {
      userChangedRef.current = false;
      return;
    }
    setSelected(defaultValue);
    setInputValue(defaultValue?.tag || '');
  }, [defaultValue]);

  const search = useCallback(
    async (event: { query: string }) => {
      const where = event.query ? { tag: { contains: event.query } } : {};

      const { data } = await query({
        variables: {
          skip: 0,
          take: SELECTION_LIMIT,
          where,
        },
      });

      setFiltered(data?.recipients?.items ?? []);
    },
    [query],
  );

  const handleSelect = (e: { value: Recipient }) => {
    userChangedRef.current = true;
    setSelected(e.value);
    setInputValue(e.value?.tag as string);
    if (onSelect) {
      onSelect(e as any);
    }
  };

  const handleChange = (e: { value: string }) => {
    userChangedRef.current = true;
    setInputValue(e.value as string);
    externalOnChange?.(e as any);
  };
  return (
    <div
      className={`[ recipients-auto-complete ] [${className ? ` ${className}` : ''} label-input-group ]`}
    >
      {label && <label>{label}</label>}
      <AutoComplete
        appendTo="self"
        suggestions={filtered}
        field="tag"
        minLength={MIN_SEARCH_LENGTH}
        selectionLimit={SELECTION_LIMIT}
        completeMethod={search}
        onSelect={handleSelect}
        onChange={handleChange}
        dropdown={true}
        {...autoCompleteProps}
        value={inputValue}
      />
    </div>
  );
};
